import type { UseImageCreatorAssistantReturn } from '@/hooks/mastra/use-image-creator-assistant';
import {
  AiImageAssistantPanel,
  type AiImageAssistantPanelProps,
  type ThreadItem,
} from './ai-image-assistant-panel';
import {
  type EditedImageParams,
  useAiImageAssistantEditor,
} from './hooks/use-ai-image-assistant-editor';
import type { ConversationItem } from './types';

type PanelProvidedProps =
  | 'conversation'
  | 'cropModal'
  | 'expiredImageIds'
  | 'isGenerating'
  | 'isLoadingHistory'
  | 'isLoadingThreads'
  | 'onCreateThread'
  | 'onHistoryOpenChange'
  | 'onImageError'
  | 'onOpenCropRequest'
  | 'onSelectThread'
  | 'onStop'
  | 'threadId'
  | 'threads';

export interface AiImageAssistantPanelContainerProps
  extends Omit<AiImageAssistantPanelProps, PanelProvidedProps> {
  assistant?: UseImageCreatorAssistantReturn;

  conversation?: ConversationItem[];
  isGenerating?: boolean;
  isLoadingHistory?: boolean;
  onStop?: () => void;
  threads?: ThreadItem[];
  isLoadingThreads?: boolean;
  threadId?: string;
  onCreateThread?: () => void;
  onHistoryOpenChange?: (open: boolean) => void;
  onSelectThread?: (threadId: string) => void;

  onImageEdited?: (params: EditedImageParams) => void;

  showUpscale?: boolean;
  showRemoveBg?: boolean;
}

export function AiImageAssistantPanelContainer({
  assistant,
  conversation: conversationProp,
  isGenerating: isGeneratingProp,
  isLoadingHistory: isLoadingHistoryProp,
  onStop: onStopProp,
  threads: threadsProp,
  isLoadingThreads: isLoadingThreadsProp,
  threadId: threadIdProp,
  onCreateThread: onCreateThreadProp,
  onHistoryOpenChange: onHistoryOpenChangeProp,
  onSelectThread: onSelectThreadProp,
  onImageEdited,
  showUpscale,
  showRemoveBg,
  ...panelProps
}: AiImageAssistantPanelContainerProps) {
  const conversation = conversationProp ?? assistant?.conversation ?? [];
  const editor = useAiImageAssistantEditor({
    assistant,
    conversation,
    onImageEdited,
  });

  const cropModal = editor.cropImage
    ? {
        imageUrl: editor.cropImage.imageUrl,
        isOpen: editor.isCropModalOpen,
        onSave: editor.saveCrop,
        onCancel: editor.closeCropModal,
        showUpscale: showUpscale ?? !!assistant,
        showRemoveBg: showRemoveBg ?? !!assistant,
        saveLabel: editor.isSavingCrop ? 'Saving...' : 'Save to Asset Library',
      }
    : null;

  return (
    <AiImageAssistantPanel
      {...panelProps}
      conversation={conversation}
      isGenerating={isGeneratingProp ?? assistant?.isGenerating ?? false}
      isLoadingHistory={isLoadingHistoryProp ?? assistant?.isLoadingHistory ?? false}
      creditStatus={assistant?.creditStatus}
      onStop={onStopProp ?? assistant?.stop}
      threads={threadsProp ?? assistant?.threads}
      isLoadingThreads={isLoadingThreadsProp ?? assistant?.isLoadingThreads}
      threadId={threadIdProp ?? assistant?.threadId}
      onCreateThread={onCreateThreadProp ?? assistant?.createThread}
      onHistoryOpenChange={
        onHistoryOpenChangeProp ?? (assistant ? () => void assistant.refreshThreads() : undefined)
      }
      onSelectThread={onSelectThreadProp ?? assistant?.selectThread}
      expiredImageIds={editor.expiredImageIds}
      onImageError={editor.markImageExpired}
      onOpenCropRequest={editor.openCropModalForImage}
      cropModal={cropModal}
    />
  );
}
